import { LoadingButton } from "../../button/loading-button";

export type TemplateOption = {
  id: string;
  element_name: string;
  data?: string;
  params_count?: number;
};

interface ConversationTemplateModalProps {
  isOpen: boolean;
  templates: TemplateOption[];
  selectedTemplateId: string;
  templatesLoading?: boolean;
  isSending?: boolean;
  onClose: () => void;
  onSelectTemplate: (templateId: string) => void;
  onSend: () => void;
}

const resolveTemplateParamsCount = (template?: TemplateOption): number => {
  const parsed = Number(template?.params_count);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
};

export function ConversationTemplateModal({
  isOpen,
  templates,
  selectedTemplateId,
  templatesLoading = false,
  isSending = false,
  onClose,
  onSelectTemplate,
  onSend,
}: ConversationTemplateModalProps) {
  if (!isOpen) return null;

  const selectedTemplate = templates.find(
    (template) => template.id === selectedTemplateId,
  );
  const selectedTemplateParamsCount =
    resolveTemplateParamsCount(selectedTemplate);
  const hasSelectedTemplateWithParams = selectedTemplateParamsCount > 0;
  const blockedTemplatesCount = templates.filter(
    (template) => resolveTemplateParamsCount(template) > 0,
  ).length;

  const sendDisabled =
    isSending ||
    templatesLoading ||
    !selectedTemplate ||
    hasSelectedTemplateWithParams;

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!isSending) {
          onClose();
        }
      }}
    >
      <div
        className="modal-card-lg space-y-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Enviar template WhatsApp
          </h2>
          <p className="text-sm text-slate-600">
            Selecione um template aprovado para enviar ao contato.
          </p>
        </div>

        <div className="space-y-2">
          <label className="form-label">Template</label>
          <select
            className="form-input"
            value={selectedTemplateId}
            onChange={(event) => onSelectTemplate(event.target.value)}
            disabled={templatesLoading || isSending}
          >
            <option value="">
              {templatesLoading
                ? "Carregando templates..."
                : templates.length > 0
                  ? "Selecione um template"
                  : "Nenhum template encontrado"}
            </option>
            {templates.map((template) => {
              const paramsCount = resolveTemplateParamsCount(template);
              const isBlockedByParams = paramsCount > 0;
              const blockedLabel = isBlockedByParams
                ? ` (bloqueado: ${paramsCount} parâmetro(s))`
                : "";

              return (
                <option
                  key={template.id}
                  value={template.id}
                  disabled={isBlockedByParams}
                >
                  {template.element_name}
                  {blockedLabel}
                </option>
              );
            })}
          </select>
          {blockedTemplatesCount > 0 && (
            <p className="text-xs text-amber-700">
              {blockedTemplatesCount} template(s) com parâmetros estão
              bloqueados nesta versão.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="form-label">Texto do template</label>
          <textarea
            className="form-textarea min-h-[180px]"
            value={selectedTemplate?.data ?? ""}
            readOnly
            placeholder="O texto do template será exibido aqui."
          />
          {hasSelectedTemplateWithParams && (
            <p className="text-xs text-red-600">
              Templates com parâmetros não podem ser enviados por essa tela.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isSending}
          >
            Cancelar
          </button>
          <LoadingButton
            type="button"
            onClick={onSend}
            className="btn btn-neutral"
            isLoading={isSending}
            loadingLabel="Enviando"
            disabled={sendDisabled}
          >
            Enviar template
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}
